/**
 * "What am I editing" — one answer, for the whole editor (ADR-2B).
 *
 * Round 1 held two independent view-state values in the editor: which block was
 * selected in the preview, and which row was expanded in the pane. They could
 * disagree, and nothing connected one to the other. R42/R55/R63 now want the same
 * properties panel to open from either place, so this hook keeps a single target
 * plus where it was opened from, and both entry points write to it.
 *
 * It also owns the focus contract (NFR A1): the panel takes focus when it opens,
 * and closing it hands focus back to that field's row in the pane. The row stays
 * mounted throughout now that the panel is its own column (spec R59) — but the
 * return target is still a *field id* the row claims for itself, not a stored DOM
 * node: the field search can filter that row out and back in between the two
 * moments, and an id survives that where a node reference would not.
 *
 * No card state lives here: this is view state, and view state is not card data
 * (ADR-2D).
 */

import { useCallback, useState } from 'react';

import { FieldId } from '../types';

/** Where the open panel was opened from — a pane row, or a preview block. */
export type InspectorSource = 'row' | 'block';

interface InspectorView {
  target: FieldId | null;
  source: InspectorSource | null;
  focusReturnFieldId: FieldId | null;
}

export interface InspectorState extends InspectorView {
  /**
   * Opens a field's properties from its pane row — or closes the panel again
   * when that row's chevron is the thing that opened it, so the affordance
   * toggles rather than doing nothing on the second press.
   */
  openFromRow: (fieldId: FieldId) => void;
  /** Opens a field's properties by selecting its block in the preview (R55). */
  openFromBlock: (fieldId: FieldId) => void;
  /**
   * The panel's back control (R42): the properties column closes — the field list
   * never left — and the field's row takes focus, because the user asked to leave
   * the panel.
   */
  back: () => void;
  /**
   * Clicking the preview outside any block (R58). Closes without moving focus:
   * the user clicked the card, and yanking focus into the pane after a click
   * somewhere else is not something they asked for.
   */
  close: () => void;
  /**
   * Drops the target when the field itself goes away (unticked, or its block
   * deleted). No focus return: the row the user was working from is either gone
   * or no longer holds a properties affordance.
   */
  dismiss: (fieldId: FieldId) => void;
  /** Called by the row once it has taken focus back, so it happens only once. */
  acknowledgeFocusReturn: () => void;
}

const CLOSED: InspectorView = { target: null, source: null, focusReturnFieldId: null };

export const useInspector = (): InspectorState => {
  const [view, setView] = useState<InspectorView>(CLOSED);

  const openFromRow = useCallback((fieldId: FieldId) => {
    setView((current) =>
      current.target === fieldId && current.source === 'row'
        ? { target: null, source: null, focusReturnFieldId: fieldId }
        : { target: fieldId, source: 'row', focusReturnFieldId: null }
    );
  }, []);

  const openFromBlock = useCallback((fieldId: FieldId) => {
    setView({ target: fieldId, source: 'block', focusReturnFieldId: null });
  }, []);

  const back = useCallback(() => {
    setView((current) =>
      current.target === null
        ? current
        : { target: null, source: null, focusReturnFieldId: current.target }
    );
  }, []);

  const close = useCallback(() => {
    setView((current) => (current.target === null ? current : CLOSED));
  }, []);

  const dismiss = useCallback((fieldId: FieldId) => {
    setView((current) => (current.target === fieldId ? CLOSED : current));
  }, []);

  const acknowledgeFocusReturn = useCallback(() => {
    setView((current) =>
      current.focusReturnFieldId === null ? current : { ...current, focusReturnFieldId: null }
    );
  }, []);

  return {
    target: view.target,
    source: view.source,
    focusReturnFieldId: view.focusReturnFieldId,
    openFromRow,
    openFromBlock,
    back,
    close,
    dismiss,
    acknowledgeFocusReturn,
  };
};
